import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

type Status = 'verifying' | 'success' | 'already' | 'error';

const VerifyEmailPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('verifying');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const uid = params.get('uid');
    const token = params.get('token');
    if (!uid || !token) {
      setStatus('error');
      setErrorMessage('This verification link is incomplete.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(API_ENDPOINTS.AUTH.VERIFY_EMAIL, {
          uid,
          token,
        });
        if (cancelled) return;
        if (res.data?.tokens) {
          // Reason: backend returned JWTs on successful verification —
          // sign the user in directly so they land on /home.
          const { access, refresh } = res.data.tokens;
          localStorage.setItem('access_token', access);
          localStorage.setItem('refresh_token', refresh);
          axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
          setStatus('success');
          // Tiny delay so the success state is visible.
          setTimeout(() => navigate('/home', { replace: true }), 800);
        } else if (res.data?.already_verified) {
          setStatus('already');
        } else {
          setStatus('success');
        }
      } catch (err: any) {
        if (cancelled) return;
        setStatus('error');
        setErrorMessage(
          err?.response?.data?.detail ||
            'This verification link is invalid or has expired.',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        height: '100dvh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        alignItems: 'flex-start',
        py: { xs: 6, sm: 10 },
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography
          component="h1"
          sx={{
            fontFamily:
              'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
            fontWeight: 700,
            fontSize: { xs: 36, sm: 44 },
            mb: 2,
          }}
        >
          Confirming your email
        </Typography>

        {status === 'verifying' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body1">Hang tight…</Typography>
          </Box>
        )}

        {status === 'success' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            All done. Signing you in…
          </Alert>
        )}

        {status === 'already' && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              This account is already confirmed. Please log in.
            </Alert>
            <Button
              component={Link}
              to="/login"
              variant="contained"
              fullWidth
              sx={{ py: 1.5 }}
            >
              Go to log in
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
            <Typography variant="body2" sx={{ mb: 2, color: 'rgba(0,0,0,0.7)' }}>
              You can request a fresh link from the sign-up page.
            </Typography>
            <Button
              component={Link}
              to="/register"
              variant="outlined"
              fullWidth
              sx={{ py: 1.5, mb: 1.5 }}
            >
              Back to sign-up
            </Button>
            <Button
              component={Link}
              to="/login"
              variant="text"
              fullWidth
              sx={{ py: 1.5 }}
            >
              Log in instead
            </Button>
          </>
        )}
      </Box>
    </Container>
  );
};

export default VerifyEmailPage;
